import { JOURNEYNAME, LEADID, INGRESS, CUSTOM_LEADID, COCP } from "../common/constants";
import { isCOCP } from "../native/utility";
import clickStreamInstance from "./../native/clickStreamWrapper";
import { COMMON_CONSTANTS, COMMON_SECTION_NAMES } from "./constants";
import { getApiValueKeys } from "./helper";

class AnalyticsProcessor {
	#_templateName;
	#_trackAnalyticsValueBehaviour;
	#_customLabelKycRef = "$.form.7018";
	#_customLabelKycRefAlt = "$.localStorage.skycvaluemap.7018.fieldValue"
	#_category = {
		start: "skyc_start",
		pack: "skyc_start_pack_selection",
		nms: "skyc_start_number_selection",
		sim: "skyc_start_sim_verification",
		kyc: "skyc_start_digital_kyc",
		thankyou: "thankyou_page",
	};

	constructor() {
		this.#_templateName = "";
		this.#_trackAnalyticsValueBehaviour = {};
	}

	resetTemplate(templateName) {
		this.#_templateName = templateName;
	}

	getJourney() {
		/* based on pageData */
		return (isCOCP(this.#_templateName)) ? COCP : `${getApiValueKeys(JOURNEYNAME)}`; /* lob to be added */
	}

	getPreviousPageCategory() {
		const category = this.getEventCategory() || "def";
		switch (category) {
			case this.#_category.start:
				return  getApiValueKeys(INGRESS) || `thankyou_page`;
			case this.#_category.pack:
			case this.#_category.sim:
				return `${this.#_category.start}`;
			case this.#_category.nms:
				return `${this.#_category.pack}`;
			case this.#_category.kyc:
				return `${this.#_category.nms}`;
			case this.#_category.thankyou:
				return `${this.#_category.kyc}`;
			default:
				return "";
		}
	}

	getEventCategory(analyticsObject) {
		/* based on template */
		if(analyticsObject) {
			const templateName = this.#_templateName;
			const { appendString = "" } = analyticsObject;
			const arr = COMMON_SECTION_NAMES.filter((item)=> templateName.includes(item))
			switch (arr[0]) {
				case COMMON_CONSTANTS.VerifyMobileNumber:
					return `${this.#_category.start}${appendString || ""}`;
				case COMMON_CONSTANTS.PlanSelection:
					return `${this.#_category.pack}${appendString || ""}`;
				case COMMON_CONSTANTS.VerifySim:
					return `${this.#_category.sim}${appendString || ""}`;
				case COMMON_CONSTANTS.NumberSelection:
					return `${this.#_category.nms}${appendString || ""}`;
				case COMMON_CONSTANTS.DigitalKyc:
					return `${this.#_category.kyc}${appendString || ""}`;
				case COMMON_CONSTANTS.ThankYou:
					return `${this.#_category.thankyou}${
						appendString || ""
					}`;
				case COMMON_CONSTANTS.ThankYouFailure:
					return `${this.#_category.thankyou}${
						appendString || ""
					}`;
				default:
					return "";
			}
		}
		return "";
	}

	getSection(analyticsObject) {
		/* based on sectionData */
		const {metric, sectionId = ""} = analyticsObject;
		if (metric?.section) {
			return metric?.section;
		}
		if(sectionId) {
			const arr = COMMON_SECTION_NAMES.filter((item)=> sectionId.includes(item))
			switch (arr[0]) {
				case COMMON_CONSTANTS.StartSubmit:
				case COMMON_CONSTANTS.VerifyMobileNumber:
					return "verification";
				case COMMON_CONSTANTS.OtpAdhaarModal:
				case COMMON_CONSTANTS.OtpModalStart:
					return "otp_pop_up";
				case COMMON_CONSTANTS.OtpModalReferre:
					return "local_reference_otp_pop_up";
				case COMMON_CONSTANTS.FinalOtpModal:
					return "final_otp_pop_up";
				case COMMON_CONSTANTS.KycSubmitStep1:
				case COMMON_CONSTANTS.PlanSelection:
					return "pack_selection";
				case COMMON_CONSTANTS.SubmitStep2:
				case COMMON_CONSTANTS.NumberSelection:
					return "number_selection";
				case COMMON_CONSTANTS.IdVerification:
					return "id_verification_method";
				case COMMON_CONSTANTS.CheckBoxModal:
					return "aadhaar_tnc_pop_up";
				case COMMON_CONSTANTS.OtherDetails:
					return "other_details";
				case COMMON_CONSTANTS.PersonalDetails:
					return "personal_details";
				case COMMON_CONSTANTS.LocalReference:
					return "local_reference";
				case COMMON_CONSTANTS.PhotoVerification:
					return "live_photo";
				case COMMON_CONSTANTS.VideoCapture:
					return "live_video";
				case COMMON_CONSTANTS.SubmitStep3:
					return "kyc_submission";
				case COMMON_CONSTANTS.ThankYouAck:
				case COMMON_CONSTANTS.ThankYou:
					return "request_confirmation";
				case COMMON_CONSTANTS.CustomerDeclaration:
					return "customer_declaration";
				case COMMON_CONSTANTS.SimDetails:
					return "sim_details";
				case COMMON_CONSTANTS.VerifyUpcCode:
					return "verify_upc_code";
				case COMMON_CONSTANTS.CommonErrorModal:
					return "error_pop_up";
				case COMMON_CONSTANTS.LocationFetch:
					return "location_pop_up";
				default:
					return "";
			}
		}
		return ""
	}

	getSubSection(analyticsObject) {
		/* based on attributes */
		return analyticsObject?.metric?.subSection || "";
	}

	getEventAction(analyticsObject) {
		/* based on userAction */
		return analyticsObject?.metric?.action || "";
	}

	getEventLabel(analyticsObject) {
		/* based on fieldData */
		return analyticsObject?.metric?.eventLabel || "";
	}

	getEventValue(analyticsObject) {
		/* based on fieldData */
		return analyticsObject?.metric?.eventValue || "";
	}

	getCustomValue(analyticsObject) {
		return analyticsObject?.metric?.customValue || "";
	}

	getCustomLabel(analyticsObject) {
		const category = this.getEventCategory(analyticsObject);
		if (category === this.#_category.kyc || category === this.#_category.thankyou)
			return getApiValueKeys(this.#_customLabelKycRef) || getApiValueKeys(this.#_customLabelKycRefAlt) || "";
		return analyticsObject?.metric?.customLabel || "";
	}

	createAnalyticsObject(analyticsObject) {
		const leadId = getApiValueKeys(LEADID) || getApiValueKeys(CUSTOM_LEADID);
		return {
			ppn: this.getPreviousPageCategory(),
			journey: this.getJourney(analyticsObject),
			eventCategory: this.getEventCategory(analyticsObject),
			section: this.getSection(analyticsObject),
			subSection: this.getSubSection(analyticsObject),
			eventAction: this.getEventAction(analyticsObject),
			eventLabel: this.getEventLabel(analyticsObject),
			eventValue: this.getEventValue(analyticsObject),
			customValue: this.getCustomValue(analyticsObject),
			customLabel: this.getCustomLabel(analyticsObject),
			cpn: this.getEventCategory(analyticsObject),
			eVar8: leadId,
			leadId: leadId,
			prop4: analyticsObject?.metric?.prop4 || "",
			planRental : analyticsObject?.metric?.prop4 || "",
			prop26: analyticsObject?.metric?.prop26 || "",
			leadExtraData : analyticsObject?.metric?.prop26 || "",
			eVar32: analyticsObject?.metric?.eVar32 || "",
			error : analyticsObject?.metric?.eVar32 || "",
			loadTimeInMilliseconds: analyticsObject?.metric?.loadTime || ""
		};
	}

	saveAnalyticsValueBehaviour(analyticsObject) {
		this.#_trackAnalyticsValueBehaviour[analyticsObject?.analyticsId] = {
			value:
				analyticsObject.metric.eventValue || analyticsObject.metric.eventLabel,
		};
	}

	checkAnalyticsValueBehaviour(analyticsObject) {
		const oldValue =
			this.#_trackAnalyticsValueBehaviour[analyticsObject?.analyticsId]
				?.value || "";
		const newValue =
			analyticsObject.metric.eventValue || analyticsObject.metric.eventLabel;
		if (oldValue != newValue) {
			return true;
		}
		return false;
	}

	captureAnalytics(analyticsObject) {
		try {
			this.createAndSendAnalytics(analyticsObject);
		} catch (err) {
			console.error(`Issue Analytics in capturing : ${err}`);
		}
	}

	createAndSendAnalytics(analyticsObject) {
		try {
			const analyticsModel = this.createAnalyticsObject(analyticsObject);
			clickStreamInstance().sendClickStream(analyticsModel);
		} catch (err) {
			console.error(`Issue Analytics click stream : ${err}`);
		}
	}
}

export { AnalyticsProcessor };
